import { Button, Code, CopyButton, Icon } from '@deque/cauldron-react';
import React, { type RefObject, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styles from './GuideView.css';
import { type CodeBlock, type Guide } from './guide';

interface GuideViewProps {
  guide: Guide;
  backLabel: string;
  onBack: () => void;
  onDownload: () => void;
  onReset: () => void;
  headingRef: RefObject<HTMLHeadingElement | null>;
}

const HIGHLIGHT_LANGUAGE: Record<
  CodeBlock['language'],
  'javascript' | undefined
> = { json: 'javascript', shell: undefined, text: undefined };

const GuideView = ({
  guide,
  backLabel,
  onBack,
  onDownload,
  onReset,
  headingRef
}: GuideViewProps): React.ReactElement => {
  const { t } = useTranslation();
  const idPrefix = useId();
  const blockLabelId = (sectionIndex: number, blockIndex: number) =>
    `${idPrefix}-${sectionIndex}-${blockIndex}`;

  return (
    <>
      <h1 ref={headingRef} tabIndex={-1} className={styles.guideHeading}>
        {guide.title}
      </h1>
      <p className={styles.intro}>{guide.intro}</p>

      {guide.summary && guide.summary.length > 0 && (
        <dl className={styles.summaryBar}>
          {guide.summary.map(item => (
            <div key={item.label} className={styles.summaryItem}>
              <dt className={styles.summaryLabel}>{item.label}:</dt>
              <dd className={styles.summaryValue}>{item.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <ol role="list" className={styles.sections}>
        {guide.sections.map((section, sectionIndex) => (
          <li key={section.heading} className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionMarker} aria-hidden="true">
                {sectionIndex + 1}
              </span>
              <h2 className={styles.sectionHeading}>{section.heading}</h2>
            </div>
            <p className={styles.sectionBody}>{section.body}</p>
            {section.link &&
              (/^\/(?!\/)/.test(section.link.href) ? (
                <Link to={section.link.href} className={styles.sectionLink}>
                  {section.link.text}
                </Link>
              ) : (
                <a href={section.link.href} className={styles.sectionLink}>
                  {section.link.text}
                </a>
              ))}
            {section.blocks.map((block, blockIndex) => (
              <div key={block.label} className={styles.block}>
                <span
                  id={blockLabelId(sectionIndex, blockIndex)}
                  className={styles.blockLabel}
                >
                  {block.label}
                </span>
                <div className={styles.blockCode}>
                  <CopyButton
                    className={styles.blockCopyButton}
                    value={block.code}
                    hideVisibleLabel
                    thin
                    notificationLabel={t('Copied')}
                  >
                    {t('Copy {{label}}', { label: block.label })}
                  </CopyButton>
                  <Code
                    scrollable
                    role="region"
                    aria-labelledby={blockLabelId(sectionIndex, blockIndex)}
                    language={HIGHLIGHT_LANGUAGE[block.language]}
                  >
                    {block.code}
                  </Code>
                </div>
              </div>
            ))}
            {section.note && (
              <p className={styles.note}>
                <strong>{t('Note:')}</strong> {section.note}
              </p>
            )}
          </li>
        ))}
      </ol>

      {guide.footer && <p className={styles.footer}>{guide.footer}</p>}

      <div className={styles.actions}>
        <div className={styles.actionsPrimary}>
          <Button variant="secondary" type="button" onClick={onBack}>
            <Icon type="arrow-left" />
            {backLabel}
          </Button>
          <Button variant="primary" type="button" onClick={onDownload}>
            <Icon type="download" />
            {t('Download guide as Markdown')}
          </Button>
        </div>
      </div>

      <p className={styles.resetLink}>
        <Button variant="link" type="button" onClick={onReset}>
          {t('Create another setup guide')}
        </Button>
      </p>
    </>
  );
};

export default GuideView;
